import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import {Box, Drawer, IconButton} from "@mui/material";
import {useRightDrawer} from "crqlar-react-common/dist/components/header/RightDrawerContext";
import {createContext, type ReactNode, useCallback, useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {useCompactDashboardLayout} from "../../../utils/responsive.util";import SidebarContent from "./SidebarContent";
import {EXPANDED_SIDEBAR_WIDTH, SIDEBAR_BUTTON_BORDER_RADIUS} from "./sidebarLayout";

const MobileSidebarContext = createContext<(() => void) | undefined>(undefined);

export const MobileSidebarButton = () => {
    const {t} = useTranslation();
    const isCompactLayout = useCompactDashboardLayout();
    const openSidebar = useContext(MobileSidebarContext);
    if (!openSidebar) return null;

    return (
        <IconButton
            id="dashboard-mobile-sidebar-button"
            className="hide-on-print"
            aria-label={t("Open dashboard navigation")}
            onClick={openSidebar}
            sx={{
                display: isCompactLayout ? "inline-flex" : "none",
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: SIDEBAR_BUTTON_BORDER_RADIUS,
                color: "primary.dark",
                bgcolor: "transparent",
                "&:hover": {bgcolor: "action.hover"},
            }}
        >
            <MenuIcon/>
        </IconButton>
    );
};

const mobileControlSx = {
    width: 40,
    height: 40,
    bgcolor: "background.paper",
    color: "primary.dark",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.12)",
    "&:hover": {bgcolor: "action.hover"},
};

interface MobileSidebarProps {
    children: ReactNode;
}

const MobileSidebar = ({children}: MobileSidebarProps) => {
    const {t} = useTranslation();
    const {pathname} = useLocation();
    const {openRightDrawer} = useRightDrawer();
    const isCompactLayout = useCompactDashboardLayout();
    const [open, setOpen] = useState(false);
    const openSidebar = useCallback(() => setOpen(true), []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <MobileSidebarContext.Provider value={openSidebar}>
            {children}

            {openRightDrawer && (
                <Box
                    className="hide-on-print"
                    sx={{
                        display: isCompactLayout ? "block" : "none",
                        position: "fixed",
                        top: "calc(var(--banner-offset, 0px) + 8px)",
                        right: 12,
                        zIndex: 450,
                    }}
                >
                    <IconButton
                        aria-label={t("Open page tools")}
                        onClick={openRightDrawer}
                        sx={mobileControlSx}
                    >
                        <MenuOpenIcon/>
                    </IconButton>
                </Box>
            )}

            <Drawer
                open={open}
                onClose={() => setOpen(false)}
                ModalProps={{keepMounted: true}}
                PaperProps={{
                    role: "navigation",
                    "aria-label": t("Dashboard navigation"),
                    sx: {width: EXPANDED_SIDEBAR_WIDTH},
                }}
            >
                <SidebarContent expanded/>
            </Drawer>
        </MobileSidebarContext.Provider>
    );
};

export default MobileSidebar;
